export const BOOKING_FLOW_ANALYTICS_SCHEMA_VERSION = 2 as const;

export const BOOKING_FLOW_CLIENT_EVENT_TYPES = [
  'date_selected',
  'headcount_selected',
  'time_selected',
  'availability_result_presented',
  'contact_form_started',
  'reservation_validation_blocked',
  'reservation_submit_attempted',
  'reservation_submit_transport_failed',
] as const;

export const BOOKING_FLOW_BACKEND_EVENT_TYPES = [
  'reservation_request_rejected',
  'reservation_request_created',
] as const;

export const BOOKING_FLOW_ANALYTICS_EVENT_TYPES = [
  ...BOOKING_FLOW_CLIENT_EVENT_TYPES,
  ...BOOKING_FLOW_BACKEND_EVENT_TYPES,
] as const;

export type BookingFlowClientEventType = typeof BOOKING_FLOW_CLIENT_EVENT_TYPES[number];
export type BookingFlowBackendEventType = typeof BOOKING_FLOW_BACKEND_EVENT_TYPES[number];
export type BookingFlowAnalyticsEventType = typeof BOOKING_FLOW_ANALYTICS_EVENT_TYPES[number];

export const AVAILABILITY_RESULTS = [
  'available',
  'unavailable',
  'outside_bookable_window',
  'capacity_full',
] as const;
export type AvailabilityResult = typeof AVAILABILITY_RESULTS[number];

// Phase 2 keeps party-size cardinality bounded while preserving the small-party
// detail that is operationally useful to reservation teams. The public booking
// contract accepts at most 30 guests, while the analytics validator remains
// structurally defensive up to 99 for already-persisted V2 events.
export const BOOKING_FLOW_ANALYTICS_PARTY_SIZE_BUCKETS = [
  '1',
  '2',
  '3-4',
  '5-6',
  '7-8',
  '9-12',
  '13+',
] as const;
export type BookingFlowAnalyticsPartySizeBucket = typeof BOOKING_FLOW_ANALYTICS_PARTY_SIZE_BUCKETS[number];

export const toBookingFlowAnalyticsPartySizeBucket = (headcount: number): BookingFlowAnalyticsPartySizeBucket => {
  if (!Number.isInteger(headcount) || headcount < 1 || headcount > 99) {
    throw new Error('BOOKING_FLOW_ANALYTICS_HEADCOUNT_OUT_OF_RANGE');
  }
  if (headcount === 1) return '1';
  if (headcount === 2) return '2';
  if (headcount <= 4) return '3-4';
  if (headcount <= 6) return '5-6';
  if (headcount <= 8) return '7-8';
  if (headcount <= 12) return '9-12';
  return '13+';
};

export const VALIDATION_BLOCK_REASONS = [
  'missing_required_fields',
  'invalid_date',
  'invalid_time',
  'invalid_headcount',
] as const;
export type ValidationBlockReason = typeof VALIDATION_BLOCK_REASONS[number];

export const TRANSPORT_FAILURE_KINDS = [
  'network_error',
  'timeout',
  'aborted',
] as const;
export type TransportFailureKind = typeof TRANSPORT_FAILURE_KINDS[number];

export const RESERVATION_REQUEST_REJECTION_KINDS = [
  'outside_bookable_window',
  'capacity_full',
  'validation_failed',
  'unit_unavailable',
] as const;
export type ReservationRequestRejectionKind = typeof RESERVATION_REQUEST_REJECTION_KINDS[number];

export const BOOKING_FLOW_RESERVATION_MODES = [
  'auto',
  'request',
] as const;
export type ReservationMode = typeof BOOKING_FLOW_RESERVATION_MODES[number];

export const TIME_SELECTION_SOURCES = [
  'suggested',
  'manual',
] as const;
export type TimeSelectionSource = typeof TIME_SELECTION_SOURCES[number];

export type EmptyBookingFlowPayload = Record<string, never>;

export interface DateSelectedPayload {
  requestedDateKey: string;
}

export interface HeadcountSelectedPayload {
  headcount: number;
}

export interface TimeSelectedPayload {
  startTimeBucket: string;
  source: TimeSelectionSource;
}

export interface AvailabilityResultPresentedPayload {
  availabilityDecisionId: string;
  requestedDateKey: string;
  startTimeBucket?: string;
  headcount: number;
  availabilityResult: AvailabilityResult;
}

export interface ReservationValidationBlockedPayload {
  reasonCode: ValidationBlockReason;
}

export interface ReservationSubmitTransportFailedPayload {
  transportFailureKind: TransportFailureKind;
}

export interface ReservationRequestRejectedPayload {
  rejectionKind: ReservationRequestRejectionKind;
  reservationMode?: ReservationMode;
}

export interface ReservationRequestCreatedPayload {
  reservationMode: ReservationMode;
  initialStatus: 'pending' | 'confirmed';
}

export interface BookingFlowClientEventInputBase<TEventType extends BookingFlowClientEventType, TPayload> {
  schemaVersion: typeof BOOKING_FLOW_ANALYTICS_SCHEMA_VERSION;
  authority: 'client';
  eventId: string;
  eventType: TEventType;
  unitId: string;
  visitorId: string;
  sessionId: string;
  bookingFlowId: string;
  occurredAtClient: string;
  clientTimezone: string;
  idempotencyKey: string;
  payload: TPayload;
}

export type BookingFlowClientEventInput =
  | (BookingFlowClientEventInputBase<'date_selected', DateSelectedPayload> & { selectionRevision: number })
  | (BookingFlowClientEventInputBase<'headcount_selected', HeadcountSelectedPayload> & { selectionRevision: number })
  | (BookingFlowClientEventInputBase<'time_selected', TimeSelectedPayload> & { selectionRevision: number })
  | (BookingFlowClientEventInputBase<'availability_result_presented', AvailabilityResultPresentedPayload> & { selectionRevision: number; availabilityRevision: number })
  | (BookingFlowClientEventInputBase<'contact_form_started', EmptyBookingFlowPayload> & { selectionRevision: number })
  | (BookingFlowClientEventInputBase<'reservation_validation_blocked', ReservationValidationBlockedPayload> & { selectionRevision: number })
  | (BookingFlowClientEventInputBase<'reservation_submit_attempted', EmptyBookingFlowPayload> & { selectionRevision: number; submitAttemptId: string })
  | (BookingFlowClientEventInputBase<'reservation_submit_transport_failed', ReservationSubmitTransportFailedPayload> & { submitAttemptId: string });

export type BookingFlowClientEventInputEventTypeMap = {
  [K in BookingFlowClientEventInput as K['eventType']]: K;
};

export type PersistedBookingFlowClientEvent = BookingFlowClientEventInput & {
  receivedAtServer: string;
  normalization?: {
    clientTimeTrusted?: boolean;
    clientTimezoneNormalized?: boolean;
  };
};

export interface BookingFlowBackendEventBase<TEventType extends BookingFlowBackendEventType, TPayload> {
  schemaVersion: typeof BOOKING_FLOW_ANALYTICS_SCHEMA_VERSION;
  authority: 'backend';
  eventId: string;
  eventType: TEventType;
  unitId: string;
  visitorId: string;
  sessionId: string;
  bookingFlowId: string;
  submitAttemptId: string;
  occurredAtServer: string;
  backendIdempotencyKey: string;
  payload: TPayload;
}

export type BookingFlowBackendEvent =
  | (BookingFlowBackendEventBase<'reservation_request_rejected', ReservationRequestRejectedPayload> & { reservationId?: never })
  | (BookingFlowBackendEventBase<'reservation_request_created', ReservationRequestCreatedPayload> & { reservationId: string });

export type BookingFlowAnalyticsEvent = PersistedBookingFlowClientEvent | BookingFlowBackendEvent;
